import { createApi } from '@reduxjs/toolkit/query/react';

import {
  IEmailVerifyRequest,
  IForgotPasswordByPhoneRequest,
  IForgotPasswordByPhoneResponse,
  IForgotPasswordRequest,
  IForgotPasswordResponse,
  ILoginRequest,
  IPushStatusRequest,
  IPushStatusResponse,
  IRecoverPasswordByPhoneRequest,
  IRecoverPasswordByPhoneResponse,
  IRecoverPasswordRequest,
  IRecoverPasswordResponse,
  IRegisterPushStatusResponse,
  IRegisterRequest,
  IRegisterResponse,
  IVerifyOtpRequest,
} from '@/interfaces/auth';
import { ILoginResponse } from '@/interfaces/user';

import { baseQuery } from './baseQuery';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    register: builder.mutation<IRegisterResponse, IRegisterRequest>({
      query: (data) => ({
        url: 'auth/register',
        method: 'POST',
        body: data,
      }),
    }),
    emailVerify: builder.query<unknown, IEmailVerifyRequest>({
      query: (data) => ({
        url: `email/verify/${data.verifyCode}`,
        method: 'GET',
      }),
    }),
    login: builder.mutation<ILoginResponse, ILoginRequest>({
      query: (data) => ({
        url: `auth/login`,
        method: 'POST',
        body: data,
      }),

      transformResponse(res: ILoginResponse) {
        const accessToken = res.access_token;

        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
        }

        return res;
      },
    }),
    verifyOtp: builder.mutation<ILoginResponse, IVerifyOtpRequest>({
      query: (data) => ({
        url: 'auth/login-sms/confirm',
        method: 'POST',
        body: data,
      }),

      transformResponse(res: ILoginResponse) {
        const accessToken = res.access_token;

        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
        }

        return res;
      },
    }),
    resendOtp: builder.mutation<ILoginResponse, { challenge_token: string }>({
      query: (data) => ({
        url: 'auth/login-sms/resend',
        method: 'POST',
        body: data,
      }),
    }),
    pushStatus: builder.query<IPushStatusResponse, IPushStatusRequest>({
      query: (data) => ({
        url: 'auth/login-sms/push-status',
        method: 'GET',
        params: { challenge_token: data.challenge_token },
      }),

      transformResponse(res: IPushStatusResponse) {
        if (res.status === 'approved' && res.access_token) {
          localStorage.setItem('accessToken', res.access_token);
        }
        return res;
      },
    }),
    registerPushStatus: builder.query<
      IRegisterPushStatusResponse,
      IPushStatusRequest
    >({
      query: (data) => ({
        url: 'auth/register/push-status',
        method: 'GET',
        params: { challenge_token: data.challenge_token },
      }),
    }),
    forgotPassword: builder.mutation<
      IForgotPasswordResponse,
      IForgotPasswordRequest
    >({
      query: (data) => ({
        url: 'auth/forget-password',
        method: 'POST',
        body: data,
      }),
    }),
    recoverPassword: builder.mutation<
      IRecoverPasswordResponse,
      IRecoverPasswordRequest
    >({
      query: (data) => ({
        url: 'auth/reset-password',
        method: 'POST',
        body: data,
      }),
    }),
    forgotPasswordByPhone: builder.mutation<
      IForgotPasswordByPhoneResponse,
      IForgotPasswordByPhoneRequest
    >({
      query: (data) => ({
        url: 'sms/send-reset-code',
        method: 'POST',
        body: data,
      }),
    }),
    recoverPasswordByPhone: builder.mutation<
      IRecoverPasswordByPhoneResponse,
      IRecoverPasswordByPhoneRequest
    >({
      query: (data) => ({
        url: 'reset-password-by-code',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useLazyPushStatusQuery,
  useLazyRegisterPushStatusQuery,
  useLazyEmailVerifyQuery,
  useForgotPasswordMutation,
  useRecoverPasswordMutation,
  useForgotPasswordByPhoneMutation,
  useRecoverPasswordByPhoneMutation,
} = authApi;
